
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Recipe } from '@/types';
import RecipeShareImage from './RecipeShareImage';
import { Share2, MessageCircle } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { ScrollArea } from '@/components/ui/scroll-area';

interface RecipeShareDialogProps {
  recipe: Recipe;
  trigger?: React.ReactNode;
}

const RecipeShareDialog: React.FC<RecipeShareDialogProps> = ({ recipe, trigger }) => {
  const [open, setOpen] = React.useState(false);
  const isMobile = useIsMobile();
  
  // On mobile, use a bottom drawer instead of a dialog
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>
          {trigger || (
            <div className="w-full">
              <div 
                className="bg-gradient-to-r from-vegana-primary to-vegana-secondary rounded-md p-2.5 shadow hover:shadow-md transition-all cursor-pointer flex items-center justify-between"
                title="Compartir receta"
              >
                <div className="flex items-center">
                  <Share2 className="h-5 w-5 text-white mr-2" />
                  <div className="text-white">
                    <p className="font-medium text-sm">¡Comparte esta receta!</p>
                  </div>
                </div>
                <div className="bg-white/20 p-1.5 rounded-full">
                  <MessageCircle className="h-4 w-4 text-white" />
                </div>
              </div>
            </div>
          )}
        </DrawerTrigger>
        <DrawerContent className="max-h-[80vh]">
          <DrawerHeader className="text-center">
            <DrawerTitle>Compartir Receta</DrawerTitle>
          </DrawerHeader>
          <ScrollArea className="px-4 pb-8 h-[calc(80vh-65px)]">
            <RecipeShareImage recipe={recipe} onClose={() => setOpen(false)} hideInstructions={true} />
          </ScrollArea>
        </DrawerContent>
      </Drawer>
    );
  }
  
  // On desktop, use a standard dialog
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <div className="w-full">
            <div 
              className="bg-gradient-to-r from-vegana-primary to-vegana-secondary rounded-md p-2.5 shadow hover:shadow-md transition-all cursor-pointer flex items-center justify-between"
              title="Compartir receta"
            >
              <div className="flex items-center">
                <Share2 className="h-5 w-5 text-white mr-2" />
                <div className="text-white">
                  <p className="font-medium text-sm">¡Comparte esta receta!</p>
                </div>
              </div>
              <div className="bg-white/20 p-1.5 rounded-full">
                <MessageCircle className="h-4 w-4 text-white" />
              </div>
            </div>
          </div>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>Compartir Receta</DialogTitle>
        </DialogHeader>
        <RecipeShareImage recipe={recipe} onClose={() => setOpen(false)} hideInstructions={true} />
      </DialogContent>
    </Dialog>
  );
};

export default RecipeShareDialog;
